import LockIcon from "@/assets/dark_gradient/lock.svg?react";
import GradientButton from "@/components/Common/GradientButton";
import clsxm from "@/lib/clsxm";
import {useMiscActions} from "@/store/campaignForm";
import {createCampaignForCompanyAtom} from "@/store/jotaiAtoms";
import type {TCampaignType} from "@/store/types";
import {useSetAtom} from "jotai";
import {memo, useMemo, useRef, useState, type FC, type SVGProps} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import {useHoverDirty} from "react-use";

const CampaignTile: FC<{
  campaignType: TCampaignType;
  DarkIcon?: FC<SVGProps<SVGSVGElement>>;
  DefaultIcon: FC<SVGProps<SVGSVGElement>>;
  description: string;
  hasSubscription?: boolean;
  Icon: FC<SVGProps<SVGSVGElement>>;
  LightIcon?: FC<SVGProps<SVGSVGElement>>;
  title: string;
}> = ({campaignType, DarkIcon, DefaultIcon, description, hasSubscription, Icon, LightIcon, title}) => {
  const {t} = useTranslation(["common", "campaign_creation"]);
  const navigate = useNavigate();
  const tileRef = useRef<HTMLDivElement>(null!);
  const isHovering = useHoverDirty(tileRef);
  const [isClicked, setClicked] = useState(false);
  const actions = useMiscActions();
  const setCreateCampaignForCompany = useSetAtom(createCampaignForCompanyAtom);

  const CampaignIcon = useMemo(() => {
    if (isHovering && hasSubscription) {
      return <Icon className="text-primary-dark-solid size-10" />;
    }

    if (isHovering && !hasSubscription && DarkIcon) {
      return <DarkIcon className="size-10" />;
    }

    if (!hasSubscription && !isHovering && LightIcon) {
      return <LightIcon className="size-10" />;
    }

    return <DefaultIcon className="size-10" />;
  }, [isHovering, hasSubscription]);

  return (
    <div
      ref={tileRef}
      className={clsxm(!hasSubscription && "shiny-border rounded-md")}
      onMouseMove={e => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        e.currentTarget.style.setProperty("--mouse-x", `${x}px`);
        e.currentTarget.style.setProperty("--mouse-y", `${y}px`);
      }}
      onMouseLeave={e => {
        e.currentTarget.style.removeProperty("--mouse-x");
        e.currentTarget.style.removeProperty("--mouse-y");
      }}>
      <button
        onMouseDown={() => {
          setClicked(true);
          setTimeout(() => setClicked(false), 600);
        }}
        className={clsxm(
          "group border-neutral-white-dark relative flex shrink-0 grow flex-col items-center overflow-hidden rounded-md border p-2 transition-all duration-150",
          hasSubscription ? "hover:border-primary-dark-solid" : "bg-neutral-grey-lightest",
        )}
        onClick={() => {
          if (hasSubscription) {
            actions.initNewCampaign({
              campaignType,
            });
          } else {
            navigate("/plans/");
          }

          setCreateCampaignForCompany(null);
        }}>
        {isClicked && (
          <div className="animate-ripple bg-primary-powder absolute h-full w-full -translate-x-1/2 -translate-y-1/2 opacity-30" />
        )}
        {!hasSubscription && (
          <div className="flex w-full items-center justify-start gap-1">
            <GradientButton
              Icon={<LockIcon className="h-4 w-auto shrink-0" />}
              title="campaign_creation:unlock"
              className="hover-none h-fit scale-[1.01] p-[1px]"
            />
            <span className="text-neutral-grey-dark text-[10px]">
              {t("campaign_creation:add_platform_campaigns", {platform: t(campaignType)})}
            </span>
          </div>
        )}
        <div className="flex items-center justify-center gap-3 p-5">
          <div>{CampaignIcon}</div>
          <div className="flex flex-col gap-1">
            <span
              className={clsxm(
                "text-neutral-black text-start text-xl font-semibold",
                !hasSubscription && "bg-clip-text text-transparent",
                !hasSubscription &&
                  (isHovering ? "bg-linear-(--button-gradient)" : "bg-linear-(--brand-medium-gradient)"),
                hasSubscription && isHovering && "text-primary-dark-solid",
              )}>
              {t(title)}
            </span>
            <span
              className={clsxm(
                "text-neutral-grey-dark text-start text-xs",
                !hasSubscription && "bg-clip-text text-transparent",
                !hasSubscription &&
                  (isHovering ? "bg-linear-(--button-gradient)" : "bg-linear-(--brand-medium-gradient)"),
              )}>
              {t(description)}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};

export default memo(CampaignTile);
